import { useCallback, useState } from "react";
import { ApiPortalBackend, RunType } from "../apis/ApiPortalBackend";
import { InsufficientCreditsDialog } from "../components/ui/InsufficientCreditsDialog";
import { requestWalletRefresh } from "../utils/walletRefresh";

export interface TrackedRunHandle {
    runId: string;
}

export interface StartRunOptions {
    model?: string;
    agentId?: string;
    conversationId?: string;
    imageProjectId?: string;
    inputData?: unknown;
    estimatedCost?: number;
}

export interface RunCompletionParams {
    inputTokens?: number;
    outputTokens?: number;
    cost?: number;
    width?: number;
    height?: number;
    outputAssetId?: string;
    outputData?: unknown;
}

/**
 * Chokepoint for the run/billing lifecycle (same pattern as fetchWithAuth/exportMedia) - every
 * generation flow must go through this instead of calling cProxy directly, otherwise it runs
 * without a wallet check or credit deduction. See TASKS.md, "Phase 8B".
 *
 * startRun() returns null both on insufficient balance AND on any other failure (network error,
 * missing workspace, ...) - fail closed, a broken run-tracking call must not silently let
 * generation proceed unbilled. Only the insufficient-balance case shows insufficientCreditsDialog;
 * other failures are indistinguishable from a normal request failure to the caller.
 */
export const useTrackedRun = () => {
    const [insufficientCredits, setInsufficientCredits] = useState(false);

    const startRun = useCallback(async (type: RunType, opts?: StartRunOptions): Promise<TrackedRunHandle | null> => {
        const workspaceId = await ApiPortalBackend.GetDefaultWorkspaceId();
        if (!workspaceId) return null;

        const result = await ApiPortalBackend.CreateRun({ workspaceId, type, ...opts });
        if (!result.ok) {
            if (result.reason === 'insufficient_balance') setInsufficientCredits(true);
            return null;
        }
        return { runId: result.run.id };
    }, []);

    const completeRun = useCallback(async (handle: TrackedRunHandle | null, params: RunCompletionParams = {}) => {
        if (!handle) return;
        await ApiPortalBackend.CompleteRun(handle.runId, {
            inputTokens: params.inputTokens ?? 0,
            outputTokens: params.outputTokens ?? 0,
            cost: params.cost ?? 0,
            width: params.width,
            height: params.height,
            outputAssetId: params.outputAssetId,
            outputData: params.outputData,
        });
        // Delayed because the wallet debit runs asynchronously server-side after this response.
        requestWalletRefresh(2000);
    }, []);

    const failRun = useCallback(async (handle: TrackedRunHandle | null, error: string) => {
        if (!handle) return;
        await ApiPortalBackend.FailRun(handle.runId, error);
    }, []);

    const insufficientCreditsDialog = insufficientCredits
        ? <InsufficientCreditsDialog onClose={() => setInsufficientCredits(false)} />
        : null;

    return { startRun, completeRun, failRun, insufficientCreditsDialog };
};
